import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  Upload,
  X,
  FileText,
  Send,
  CheckCircle2,
  AlertCircle,
  Save,
  RotateCcw,
  Sparkles,
  Briefcase,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";

import { Reveal } from "@/components/site/SiteLayout";
import { cn } from "@/lib/utils";
import { careersService } from "@/api/careersService";

// Validation schema
const jobApplicationSchema = z.object({
  // Personal Information
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(6, "Please enter a valid phone number"),
  location: z.string().min(2, "Please enter your location"),

  // Professional Information
  position: z.string().min(1, "Please select a position"),
  experience: z.string().min(1, "Please select your experience level"),
  currentSalary: z.string().optional(),
  expectedSalary: z.string().min(1, "Please enter your expected salary"),
  availabilityDate: z.string().optional(),

  // Skills and Qualifications
  skills: z.string().min(10, "Please describe your skills (minimum 10 characters)"),
  education: z.string().min(5, "Please enter your educational background"),
  certifications: z.string().optional(),

  // Work Information
  currentCompany: z.string().optional(),
  workMode: z.string().min(1, "Please select your preferred work mode"),
  noticePeriod: z.string().min(1, "Please enter your notice period"),

  // Additional Information
  motivation: z.string().min(20, "Please tell us why you want to join us (minimum 20 characters)"),
  portfolio: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  linkedIn: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  github: z.string().url("Please enter a valid URL").optional().or(z.literal("")),

  // Agreement
  agreement: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms and conditions",
  }),
});

function GlassCard({ className, children, ...props }) {
  return (
    <Card
      className={cn(
        "bg-white/[0.04] border-white/10 backdrop-blur-xl",
        "shadow-[0_18px_50px_rgba(0,0,0,0.4)]",
        className
      )}
      {...props}
    >
      {children}
    </Card>
  );
}

export default function JobApplication() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobSlugParam = searchParams.get("job");

  const [publishedJobs, setPublishedJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobNotice, setJobNotice] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedInfo, setSubmittedInfo] = useState(null);

  const [hasSavedDraft, setHasSavedDraft] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState({
    resume: null,
    coverLetter: null,
  });

  const form = useForm({
    resolver: zodResolver(jobApplicationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      location: "",
      position: "general",
      experience: "",
      currentSalary: "",
      expectedSalary: "",
      availabilityDate: "",
      skills: "",
      education: "",
      certifications: "",
      currentCompany: "",
      workMode: "",
      noticePeriod: "",
      motivation: "",
      portfolio: "",
      linkedIn: "",
      github: "",
      agreement: false,
    },
  });

  // Draft storage key scoped to current job position
  const getDraftKey = (slug) => `shiveda_job_draft_${slug || "general"}`;

  // Check for saved local draft on mount and when role changes
  useEffect(() => {
    try {
      const position = form.getValues("position") || "general";
      const scopedKey = getDraftKey(position);
      const scopedDraft = localStorage.getItem(scopedKey);
      const legacyDraft = localStorage.getItem("shiveda_job_application_draft");
      setHasSavedDraft(Boolean(scopedDraft || legacyDraft));
    } catch {
      // localStorage may not be accessible in private browsing
    }
  }, [form, selectedJob]);

  // Fetch published database jobs
  useEffect(() => {
    let isMounted = true;

    async function loadPublishedJobs() {
      try {
        setLoadingJobs(true);
        const jobs = await careersService.getPublishedJobs();

        if (!isMounted) return;

        setPublishedJobs(jobs || []);

        // Resolve slug from query parameter
        if (jobSlugParam) {
          const matchingJob = (jobs || []).find((j) => j.slug === jobSlugParam);
          if (matchingJob) {
            setSelectedJob(matchingJob);
            form.setValue("position", matchingJob.slug);
            setJobNotice(null);
          } else {
            // Invalid, closed, or draft slug
            setSelectedJob(null);
            form.setValue("position", "general");
            setJobNotice(
              `The position "${jobSlugParam}" is currently not active or does not exist. Your application has been directed to General Application.`
            );
          }
        } else {
          setSelectedJob(null);
          form.setValue("position", "general");
          setJobNotice(null);
        }
      } catch (err) {
        console.error("[JobApplication] Failed to load published jobs:", err);
      } finally {
        if (isMounted) {
          setLoadingJobs(false);
        }
      }
    }

    loadPublishedJobs();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobSlugParam]);

  // Handle position select changes
  const handlePositionChange = (value) => {
    form.setValue("position", value);
    if (value === "general") {
      setSelectedJob(null);
      setJobNotice(null);
      setSearchParams({});
    } else {
      const matchingJob = publishedJobs.find((j) => j.slug === value);
      if (matchingJob) {
        setSelectedJob(matchingJob);
        setJobNotice(null);
        setSearchParams({ job: matchingJob.slug });
      }
    }
    // Check if draft exists for the new role selection
    try {
      const scopedKey = getDraftKey(value);
      const legacyDraft = localStorage.getItem("shiveda_job_application_draft");
      setHasSavedDraft(Boolean(localStorage.getItem(scopedKey) || legacyDraft));
    } catch {
      // ignore
    }
  };

  const restoreDraft = () => {
    try {
      const position = form.getValues("position") || "general";
      const scopedKey = getDraftKey(position);
      const draftJson =
        localStorage.getItem(scopedKey) ||
        localStorage.getItem("shiveda_job_application_draft");

      if (draftJson) {
        const parsed = JSON.parse(draftJson);
        form.reset({
          ...parsed,
          agreement: false, // require re-confirming agreement
        });
        toast.info("Saved draft restored.");
        setHasSavedDraft(false);
      }
    } catch (err) {
      console.warn("[JobApplication] Corrupted draft encountered:", err);
      try {
        const position = form.getValues("position") || "general";
        localStorage.removeItem(getDraftKey(position));
        localStorage.removeItem("shiveda_job_application_draft");
      } catch {}
      setHasSavedDraft(false);
      toast.error("Draft was corrupted and has been reset.");
    }
  };

  const discardDraft = () => {
    try {
      const position = form.getValues("position") || "general";
      localStorage.removeItem(getDraftKey(position));
      localStorage.removeItem("shiveda_job_application_draft");
      setHasSavedDraft(false);
      toast.info("Draft discarded.");
    } catch {
      setHasSavedDraft(false);
    }
  };

  const handleSaveDraftLocally = () => {
    try {
      const currentValues = form.getValues();
      const position = currentValues.position || "general";
      const scopedKey = getDraftKey(position);
      localStorage.setItem(scopedKey, JSON.stringify(currentValues));
      toast.success(
        `Draft saved for ${selectedJob?.title || "General Application"} on this device.`
      );
      setHasSavedDraft(false);
    } catch {
      toast.error("Could not save draft locally.");
    }
  };

  const experienceOptions = [
    "0-1 years",
    "1-3 years",
    "3-5 years",
    "5-8 years",
    "8-10 years",
    "10+ years",
  ];

  const workModeOptions = ["Remote", "On-site", "Hybrid", "No preference"];

  const noticePeriodOptions = [
    "Immediate",
    "15 days",
    "30 days",
    "45 days",
    "60 days",
    "90 days",
  ];

  const handleFileUpload = (type, event) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const validExtensions = ["pdf", "doc", "docx"];
      const ext = file.name.split(".").pop().toLowerCase();
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
        toast.error("Please upload only PDF, DOC, or DOCX documents");
        return;
      }

      if (file.size > maxSize) {
        toast.error("File size must not exceed 5MB");
        return;
      }

      setUploadedFiles((prev) => ({
        ...prev,
        [type]: file,
      }));
      toast.success(`${type === "resume" ? "Resume" : "Cover Letter"} selected`);
    }
  };

  const removeFile = (type) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [type]: null,
    }));
  };

  // ------------------------------------------------------------
  // Submit Application Flow with Atomic Rollback & Safe Errors
  // ------------------------------------------------------------
  const onSubmit = async (data) => {
    if (!uploadedFiles.resume) {
      toast.error("Please upload your resume to complete your application");
      return;
    }

    let uploadedResumePath = null;
    let uploadedCoverPath = null;

    try {
      setIsSubmitting(true);

      const targetJobId = selectedJob ? selectedJob.id : null;

      // 1. Upload resume to private career-resumes bucket
      const resumeResult = await careersService.uploadResumeFile(
        uploadedFiles.resume,
        targetJobId
      );
      uploadedResumePath = resumeResult.storagePath;

      // 2. Upload optional cover letter if supplied
      let coverResult = null;
      if (uploadedFiles.coverLetter) {
        coverResult = await careersService.uploadCoverLetter(
          uploadedFiles.coverLetter,
          targetJobId
        );
        uploadedCoverPath = coverResult.storagePath;
      }

      // 3. Assemble atomic RPC payload
      const applicationPayload = {
        job_id: targetJobId,
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        location: data.location.trim(),
        experience_level: data.experience,
        current_salary: data.currentSalary ? data.currentSalary.trim() : null,
        expected_salary: data.expectedSalary.trim(),
        availability_date: data.availabilityDate ? data.availabilityDate : null,
        notice_period: data.noticePeriod,
        work_mode_preference: data.workMode,
        skills: data.skills.trim(),
        education: data.education.trim(),
        certifications: data.certifications ? data.certifications.trim() : null,
        current_company: data.currentCompany ? data.currentCompany.trim() : null,
        motivation: data.motivation.trim(),
        portfolio_url: data.portfolio ? data.portfolio.trim() : null,
        linkedin_url: data.linkedIn ? data.linkedIn.trim() : null,
        github_url: data.github ? data.github.trim() : null,
        resume_storage_path: resumeResult.storagePath,
        resume_filename: resumeResult.originalFilename,
        cover_letter_storage_path: coverResult ? coverResult.storagePath : null,
        cover_letter_filename: coverResult ? coverResult.originalFilename : null,
      };

      // 4. Submit via submit_job_application PostgreSQL RPC
      const rpcResult = await careersService.submitApplication(applicationPayload);

      if (rpcResult && rpcResult.success) {
        // Clear draft for this specific role and legacy key
        try {
          const position = form.getValues("position") || "general";
          localStorage.removeItem(getDraftKey(position));
          localStorage.removeItem("shiveda_job_application_draft");
        } catch {
          // ignore
        }

        // Set success view state
        setSubmittedInfo({
          role: rpcResult.job_title || (selectedJob ? selectedJob.title : "General Application"),
          name: `${data.firstName} ${data.lastName}`,
        });
        setIsSubmitted(true);
        toast.success("Application submitted successfully!");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        throw new Error(rpcResult?.message || "Failed to submit application.");
      }
    } catch (error) {
      console.error("[JobApplication] Submission failure:", error);

      // Roll back uploaded files immediately
      if (uploadedResumePath) {
        await careersService.rollbackUploadedFile(uploadedResumePath);
      }
      if (uploadedCoverPath) {
        await careersService.rollbackUploadedFile(uploadedCoverPath);
      }

      // Safe user-friendly error translation
      const raw = (error.message || "").toLowerCase();
      if (raw.includes("already submitted an application for this role")) {
        toast.error(
          "You have already applied for this position within the last 30 days. Our team is actively reviewing your candidacy."
        );
      } else if (raw.includes("already submitted a general application")) {
        toast.error(
          "You have already submitted a general application within the last 30 days. Our recruitment team has your profile on file."
        );
      } else if (raw.includes("not accepting applications") || raw.includes("not found")) {
        toast.error(
          "This position is no longer accepting submissions. Please select an active open role or submit a General Application."
        );
      } else if (raw.includes("deadline")) {
        toast.error("The deadline for this position has passed.");
      } else if (raw.includes("size exceeds") || raw.includes("5mb")) {
        toast.error("Your uploaded resume exceeds the 5MB file size limit.");
      } else {
        toast.error(error.message || "Failed to submit application. Please check your connection and try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ------------------------------------------------------------
  // Success Confirmation Screen
  // ------------------------------------------------------------
  if (isSubmitted) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center py-16 px-5">
        <div className="max-w-2xl w-full">
          <GlassCard className="p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute right-0 top-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />

            <div className="relative z-10 space-y-6">
              <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/30 text-green-400 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(34,197,94,0.3)]">
                <CheckCircle2 className="w-9 h-9" />
              </div>

              <div className="space-y-2">
                <Badge className="bg-primary/15 text-primary border-primary/25 font-semibold text-xs">
                  Application Received
                </Badge>
                <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  Thank You, {submittedInfo?.name || "Applicant"}!
                </h1>
                <p className="text-sm sm:text-base text-white/70 max-w-lg mx-auto">
                  Your application for{" "}
                  <span className="text-primary font-semibold">
                    {submittedInfo?.role || "the position"}
                  </span>{" "}
                  has been securely transmitted to our Talent Acquisition team.
                </p>
              </div>

              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02] text-left text-xs sm:text-sm text-white/75 space-y-3">
                <div className="font-semibold text-white flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span>Next Steps in Our Process</span>
                </div>
                <ul className="space-y-2 text-white/65">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span>Our engineering team evaluates your qualifications and experience.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span>If there is a strong alignment, we will contact you via email to schedule an initial technical conversation.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span>Your resume remains private and securely stored on our systems.</span>
                  </li>
                </ul>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/career")}
                  className="rounded-xl bg-primary text-black hover:bg-primary/90 btn-glow font-bold text-xs h-11 px-6"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Browse More Opportunities
                </Button>
                <Button
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="rounded-xl border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.08] text-xs h-11 px-6"
                >
                  Return to Home
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------
  // Main Form Screen
  // ------------------------------------------------------------
  return (
    <div className="min-h-screen py-10">
      <div className="max-w-4xl mx-auto px-5 xl:px-10">
        {/* Header */}
        <Reveal>
          <div className="mb-8">
            <Button
              onClick={() => navigate("/career")}
              variant="ghost"
              className="text-white hover:bg-white/10 mb-4 rounded-xl text-xs"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Careers
            </Button>

            <div className="flex flex-wrap items-center gap-3 mb-3">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Job Application
              </h1>
              <Badge className="bg-primary/15 text-primary border-primary/25 font-semibold text-xs py-1 px-3">
                {selectedJob ? selectedJob.title : "General Application"}
              </Badge>
            </div>

            <p className="text-white/70 text-sm sm:text-base max-w-2xl">
              Join our engineering team at Shiveda IT Services. Please complete the sections below
              to help us evaluate your background and technical proficiencies.
            </p>

            {/* Inactive or unknown job alert */}
            {jobNotice && (
              <div className="mt-4 p-4 rounded-2xl border border-amber-500/25 bg-amber-500/10 text-amber-300 text-xs sm:text-sm flex items-start gap-3">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{jobNotice}</span>
              </div>
            )}

            {/* Restorable Draft Banner */}
            {hasSavedDraft && (
              <div className="mt-4 p-4 rounded-2xl border border-primary/30 bg-primary/[0.07] text-white text-xs sm:text-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <Save className="w-4 h-4 text-primary shrink-0" />
                  <span>A saved draft application was found on this browser.</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={restoreDraft}
                    className="h-8 rounded-lg bg-primary text-black hover:bg-primary/90 text-xs font-semibold"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    Restore Draft
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={discardDraft}
                    className="h-8 rounded-lg text-white/60 hover:text-white text-xs"
                  >
                    Discard
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Reveal>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Section 1: Personal Information */}
            <Reveal delay={100}>
              <GlassCard>
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2.5 text-base sm:text-lg">
                    <div className="w-7 h-7 rounded-full bg-primary text-black text-xs font-bold flex items-center justify-center">
                      1
                    </div>
                    <span>Personal Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">First Name *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="John"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Last Name *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Doe"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Email Address *</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="john.doe@example.com"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Phone Number *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="+1 (555) 123-4567"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-xs">Current Location *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="City, State, Country"
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </GlassCard>
            </Reveal>

            {/* Section 2: Professional Information */}
            <Reveal delay={200}>
              <GlassCard>
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2.5 text-base sm:text-lg">
                    <div className="w-7 h-7 rounded-full bg-primary text-black text-xs font-bold flex items-center justify-center">
                      2
                    </div>
                    <span>Professional Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="position"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Position Applied For *</FormLabel>
                          <Select onValueChange={handlePositionChange} value={field.value || "general"}>
                            <FormControl>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 text-xs">
                                <SelectValue placeholder="Select position" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-black/95 border-white/10 text-white">
                              <SelectItem value="general" className="font-semibold text-primary">
                                General Application (Open Matching)
                              </SelectItem>
                              {publishedJobs.map((job) => (
                                <SelectItem key={job.id} value={job.slug}>
                                  {job.title} ({job.departments?.name || "Role"})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="experience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Years of Experience *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 text-xs">
                                <SelectValue placeholder="Select experience level" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-black/95 border-white/10 text-white">
                              {experienceOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="currentSalary"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Current Salary (Optional)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. ₹12,00,000"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="expectedSalary"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Expected Salary *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. ₹16,00,000"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="availabilityDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Earliest Start Date (Optional)</FormLabel>
                          <FormControl>
                            <Input
                              type="date"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="currentCompany"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Current / Recent Company (Optional)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Tech Corp Inc."
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="noticePeriod"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Notice Period *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 text-xs">
                                <SelectValue placeholder="Select notice period" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-black/95 border-white/10 text-white">
                              {noticePeriodOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="workMode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-xs">Preferred Work Mode *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 text-xs">
                              <SelectValue placeholder="Select work preference" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-black/95 border-white/10 text-white">
                            {workModeOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </GlassCard>
            </Reveal>

            {/* Section 3: Document Upload */}
            <Reveal delay={300}>
              <GlassCard>
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2.5 text-base sm:text-lg">
                    <div className="w-7 h-7 rounded-full bg-primary text-black text-xs font-bold flex items-center justify-center">
                      3
                    </div>
                    <span>Resume & Documents</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Resume Upload */}
                  <div>
                    <Label className="text-white text-xs font-semibold mb-2 block">
                      Resume / CV * <span className="text-white/50 font-normal">(PDF, DOC, DOCX — Max 5MB)</span>
                    </Label>
                    <div className="border-2 border-dashed border-white/15 rounded-2xl p-6 text-center hover:border-primary/40 transition-colors bg-white/[0.01]">
                      {uploadedFiles.resume ? (
                        <div className="flex items-center justify-between bg-white/5 rounded-xl p-3.5 border border-white/10">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-primary" />
                            <div className="text-left">
                              <span className="text-white text-xs font-medium block">
                                {uploadedFiles.resume.name}
                              </span>
                              <span className="text-[11px] text-white/50">
                                {(uploadedFiles.resume.size / (1024 * 1024)).toFixed(2)} MB
                              </span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile("resume")}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-8 w-8 text-white/40 mx-auto" />
                          <div className="text-xs text-white">
                            <label className="cursor-pointer font-semibold text-primary hover:underline">
                              Choose your resume file
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleFileUpload("resume", e)}
                                className="hidden"
                              />
                            </label>
                            <p className="text-white/50 text-[11px] mt-1">
                              File is securely encrypted in private cloud storage
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cover Letter Upload */}
                  <div>
                    <Label className="text-white text-xs font-semibold mb-2 block">
                      Cover Letter <span className="text-white/50 font-normal">(Optional — PDF, DOC, DOCX — Max 5MB)</span>
                    </Label>
                    <div className="border-2 border-dashed border-white/15 rounded-2xl p-6 text-center hover:border-primary/40 transition-colors bg-white/[0.01]">
                      {uploadedFiles.coverLetter ? (
                        <div className="flex items-center justify-between bg-white/5 rounded-xl p-3.5 border border-white/10">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-primary" />
                            <div className="text-left">
                              <span className="text-white text-xs font-medium block">
                                {uploadedFiles.coverLetter.name}
                              </span>
                              <span className="text-[11px] text-white/50">
                                {(uploadedFiles.coverLetter.size / (1024 * 1024)).toFixed(2)} MB
                              </span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile("coverLetter")}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-7 w-7 text-white/40 mx-auto" />
                          <div className="text-xs text-white">
                            <label className="cursor-pointer font-semibold text-white/80 hover:text-white">
                              Attach optional cover letter
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleFileUpload("coverLetter", e)}
                                className="hidden"
                              />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </GlassCard>
            </Reveal>

            {/* Section 4: Skills and Qualifications */}
            <Reveal delay={400}>
              <GlassCard>
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2.5 text-base sm:text-lg">
                    <div className="w-7 h-7 rounded-full bg-primary text-black text-xs font-bold flex items-center justify-center">
                      4
                    </div>
                    <span>Skills & Education</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="skills"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-xs">Technical Skills & Tools *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="List your key proficiencies (e.g. React, Node.js, TypeScript, PostgreSQL, Docker, AWS, Kubernetes, etc.)"
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[90px] text-xs"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="education"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-xs">Education & Credentials *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Degree, major, institution, graduation year, or equivalent practical training"
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[75px] text-xs"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="certifications"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-xs">Certifications (Optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="AWS Certified Solutions Architect, CKA, CMMI, etc."
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[75px] text-xs"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </GlassCard>
            </Reveal>

            {/* Section 5: Additional Information */}
            <Reveal delay={500}>
              <GlassCard>
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2.5 text-base sm:text-lg">
                    <div className="w-7 h-7 rounded-full bg-primary text-black text-xs font-bold flex items-center justify-center">
                      5
                    </div>
                    <span>Motivation & Online Profiles</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="motivation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-xs">Why Shiveda IT Services? *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us what motivates you to join Shiveda IT Services and how your goals align with our team..."
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[100px] text-xs"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="portfolio"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">Portfolio / Website (Optional)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="https://yourportfolio.com"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="linkedIn"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">LinkedIn URL (Optional)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="https://linkedin.com/in/username"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="github"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white text-xs">GitHub URL (Optional)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="https://github.com/username"
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-10 text-xs"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </GlassCard>
            </Reveal>

            {/* Terms and Submit Section */}
            <Reveal delay={600}>
              <GlassCard>
                <CardContent className="pt-6 space-y-6">
                  <FormField
                    control={form.control}
                    name="agreement"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 text-primary focus:ring-primary"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-white text-xs font-semibold cursor-pointer">
                            I certify that the information provided is accurate and agree to the recruitment terms *
                          </FormLabel>
                          <p className="text-white/55 text-[11px] leading-relaxed">
                            By submitting this application, you authorize Shiveda IT Services to review and store your candidacy details in our private database for evaluation purposes.
                          </p>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator className="bg-white/10" />

                  <div className="flex flex-col sm:flex-row gap-3 items-center">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:flex-1 bg-primary text-black hover:bg-primary/90 btn-glow rounded-xl font-bold text-xs h-11 shadow-[0_0_20px_rgba(0,194,255,0.3)]"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          <span>Transmitting Application...</span>
                        </>
                      ) : (
                        <>
                          <span>Submit Application</span>
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleSaveDraftLocally}
                      className="w-full sm:w-auto bg-white/10 text-white border-white/20 hover:bg-white/15 rounded-xl text-xs h-11 px-5"
                    >
                      <Save className="w-3.5 h-3.5 mr-1.5" />
                      <span>Save Draft Locally</span>
                    </Button>
                  </div>
                </CardContent>
              </GlassCard>
            </Reveal>
          </form>
        </Form>
      </div>
    </div>
  );
}